import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { Button } from '@/components/Button';
import { useDataStore } from '@/stores/dataStore';
import { useVoiceMediaSnapshot } from '@/lib/voiceMedia';
import { askBroadcastConsent, useBroadcastConsent } from '@/components/BroadcastConsentModal';

/**
 * A standing reminder for someone sitting in a tournament room without having
 * agreed to go on air — the room became one under them, and the one-time
 * question may have been missed (another tab, a reconnect, closed by mistake).
 * The room cannot go on air until they agree or leave; this says so and opens
 * the notice. Shown only in the tab that holds the voice media.
 */
export function BroadcastConsentBanner() {
  const { t } = useTranslation();
  const { activeChannelId } = useVoiceMediaSnapshot();
  const tournament = useDataStore((s) => {
    if (!activeChannelId) return false;
    for (const guild of Object.values(s.guilds)) {
      const channel = guild.channels.find((c) => c.id === activeChannelId);
      if (channel) return channel.tournament === true;
    }
    return false;
  });
  const asking = useBroadcastConsent((s) => s.channelId !== null);
  const answered = useBroadcastConsent((s) => s.answered);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    if (!activeChannelId || !tournament) {
      setMissing(false);
      return;
    }
    let live = true;
    api
      .getBroadcastConsent(activeChannelId)
      .then((r) => {
        if (live) setMissing(r.tournament && !r.accepted);
      })
      .catch(() => {
        // Unknown: no banner rather than a wrong one; the server still refuses.
        if (live) setMissing(false);
      });
    return () => {
      live = false;
    };
  }, [activeChannelId, tournament, answered]);

  if (!missing || asking || !activeChannelId) return null;
  return (
    <div
      role="status"
      className="fixed bottom-4 left-1/2 z-50 flex max-w-[calc(100vw-2rem)] -translate-x-1/2 items-center gap-3 rounded-xl bg-surface px-4 py-2.5 text-sm text-ink shadow-lg"
    >
      <span className="min-w-0">{t('broadcastConsent.banner')}</span>
      <Button size="sm" onClick={() => askBroadcastConsent(activeChannelId, { inRoom: true })}>
        {t('broadcastConsent.bannerAction')}
      </Button>
    </div>
  );
}
